import { DownloadProps, UseDownloadProps } from './Download.types'
import { clientDownloadStems, clientUpdateDownloadCount } from '@/client'
import { useRef, useState } from 'react'

import { DownloadAnalyticsData } from './Download.types'
import { TrialState } from '@/constants/user/userTrialState'
import { clientDownloadSoundEffects } from '@/client/sound-effects/downloadSoundEffects'
import { createDownloadLink } from '@/utils/createDownloadLink'
import { downloadTracks } from '@/services/downloadsService'
import { parse } from 'content-disposition-header'
import { sendAnalytics } from '@audio-network/analytics'
import { toast } from '@/utils/notifications/toast'
import { useSession } from '../../../context/Session'
import { useTranslation } from 'next-i18next'

const BULK = 'All Track'
const SINGLE = null
const STEMS = 'Stems'
const SOUND_EFFECTS = 'Sound Effects'

const CLICKED = 'Clicked'
const STARTED = 'Started'
const COMPLETED = 'Completed'

export const useDownload = ({
	downloadName,
	stemsInfo,
	tracks,
	trackSource,
}: Pick<
	DownloadProps,
	'stemsInfo' | 'tracks' | 'trackSource' | 'downloadName'
>): UseDownloadProps => {
	const [isDownloading, setIsDownloading] = useState(false)
	const [isFetching, setIsFetching] = useState(false)
	const { user, refreshSession } = useSession()
	const progressBarRef = useRef(null)
	const { t } = useTranslation(['app'])

	let downloadType: string

	if (tracks && Array.isArray(tracks)) {
		downloadType = BULK
	} else if (downloadName === SOUND_EFFECTS) {
		downloadType = SOUND_EFFECTS
	} else if (tracks) {
		downloadType = SINGLE
	} else if (stemsInfo) {
		downloadType = STEMS
	}

	const isOnTrial =
		user?.organisation.trialState === TrialState.ActiveWithDownloads

	const stopTrialUsersBulkDownloading = isOnTrial && downloadType === BULK

	const handleAnalytics = (action: string, fileType: string): void => {
		if (downloadType === SOUND_EFFECTS) {
			const { category, subCategory } = tracks as SoundEffect

			sendAnalytics({
				noun: 'Download',
				pastTenseVerb: action,
				eventParameters: {
					TrackSource: trackSource,
					FileType: fileType,
					SFXCategory: category,
					SFXSubCategory: subCategory,
				},
			})
			return
		}

		// For other downloads, get the relevant track data
		const firstTrack = Array.isArray(tracks) ? tracks[0] : (tracks as Track)
		const trackData = downloadType === STEMS ? stemsInfo?.track : firstTrack

		const analyticsData: DownloadAnalyticsData = {
			TrackSource: trackSource,
			FileType: stemsInfo ? undefined : fileType,
			...(trackData && {
				Catalogue: trackData.album.catalogue.name,
				Genre: trackData.album.genre,
				...(downloadType === SINGLE && {
					TrackId: trackData.id,
					TrackTitle: trackData.title,
					AlbumNo: trackData.album.number,
				}),
			}),
		}

		sendAnalytics({
			adjective: downloadType || undefined,
			noun: 'Download',
			pastTenseVerb: action,
			eventParameters: analyticsData,
		})
	}

	const handleDownload = async (fileType = 'wav'): Promise<void> => {
		if (isDownloading) {
			return
		}

		handleAnalytics(CLICKED, fileType)

		if (stopTrialUsersBulkDownloading) {
			toast.error(t('bulk-download-unavailable-for-trial-user'), {
				'data-testid': 'bulk-download-unavailable-toast',
			})
			return
		}

		setIsFetching(true)
		setIsDownloading(true)

		try {
			if (progressBarRef.current) {
				progressBarRef.current.value = 0
			}

			// Get the download response
			const response = await getDownloadResponse(fileType)
			if (!response?.ok) {
				throw new Error('Download failed')
			}

			setIsFetching(false)

			if (downloadType !== SINGLE && downloadType !== SOUND_EFFECTS) {
				handleAnalytics(STARTED, fileType)
			}

			// Process the download data
			const { blob, fileName } = await processDownloadData(response)

			// Update download count and create download link
			await updateDownloadCountAndCreateLink(blob, fileName, fileType)

			if (downloadType !== SINGLE && downloadType !== SOUND_EFFECTS) {
				handleAnalytics(COMPLETED, fileType)
			}
		} catch (error) {
			toast.error(t('download-failure'), {
				'data-testid': 'download-failure-toast',
			})
		} finally {
			setIsDownloading(false)
			setIsFetching(false)
			if (user?.organisation.salesStatus === 'Trial') {
				refreshSession()
				if (user.organisation.remainingDownloads === 1) {
					toast.success(t('trial-last-download-toast'), {
						'data-testid': 'last-download-toast',
					})
				}
			}
		}
	}

	// Helper functions to separate concerns
	const getDownloadResponse = async (fileType: string): Promise<Response> => {
		if (downloadType === BULK) {
			return await downloadTracks(tracks as Track[], fileType, downloadName)
		}
		if (downloadType === SINGLE) {
			return await downloadTracks(tracks as Track, fileType)
		}
		if (downloadType === STEMS) {
			return await clientDownloadStems(stemsInfo.track)
		}
		if (downloadType === SOUND_EFFECTS) {
			const soundEffect = tracks as SoundEffect
			return await clientDownloadSoundEffects(soundEffect)
		}
		throw new Error('Invalid download type')
	}

	const processDownloadData = async (
		response: Response
	): Promise<{ blob: Blob; fileName: string }> => {
		const contentLength = response.headers.get('Content-Length')
		const totalLength = parseInt(contentLength)
		const reader = response.body.getReader()
		const chunks = []
		let receivedLength = 0

		// eslint-disable-next-line no-constant-condition
		while (true) {
			const { done, value } = await reader.read()
			if (done) break

			receivedLength += value.length
			chunks.push(value)
			const step = ((receivedLength / totalLength) * 100).toFixed(2)

			if (progressBarRef.current) {
				progressBarRef.current.value = step
			}
		}

		const blob = new Blob(chunks)
		const fileName = decodeURIComponent(
			parse(response.headers.get('Content-Disposition')).parameters.filename
		)

		return { blob, fileName }
	}

	const updateDownloadCountAndCreateLink = async (
		blob: Blob,
		fileName: string,
		fileType: string
	): Promise<void> => {
		// Update download count first
		if (downloadType === STEMS) {
			await clientUpdateDownloadCount(
				stemsInfo.track,
				fileType,
				user.id,
				user.organisation.id,
				stemsInfo.stems.length
			)
		} else if (downloadType !== SOUND_EFFECTS) {
			await clientUpdateDownloadCount(
				tracks as Track | Track[],
				fileType,
				user.id,
				user.organisation.id
			)
		}

		// Only create download link if update count succeeds
		createDownloadLink(blob, fileName)
	}

	return {
		handleDownload,
		isDownloading,
		isFetching,
		progressBarRef,
	}
}
